import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { StackScreenProps } from '@react-navigation/stack'
import { AppNavigatorParams } from '../../../navigation/types'
import { useTranslation } from 'react-i18next'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import Column from '../../../@ui/containers/grid/column'
import Row from '../../../@ui/containers/grid/row'
import { IconButton, Text } from 'react-native-paper'
import { useFocusEffect } from '@react-navigation/native'
import { useGetUserTokensQuery } from '../../../generated/graphql'
import * as Sentry from '@sentry/react-native'
import { NoDataMessage } from '../../../@ui/containers/lists/no-data'
import { PaymentMethodRow } from './components/payment-method-row'
import { PaypalConnectModal } from './components/paypal-connect.modal'
import { createStyle } from '../../../contexts/global-styles.provider'
import {
  isPaymentConnectionEnabled,
  PaymentConnectionType,
} from '../../node-admin/payment/payment-visibility.registry'

type PaymentMethodRowType = 'paypal' | 'wechat'

export function PaymentMethodsScreen({
  navigation,
}: StackScreenProps<AppNavigatorParams, 'PaymentMethods'>) {
  const { t } = useTranslation('payment-methods')
  const styles = useStyle()
  const [showPaypalModal, setShowPaypalModal] = useState(false)

  const { data, refetch, error, loading } = useGetUserTokensQuery({
    variables: {
      type: 'PAYMENT',
    },
    fetchPolicy: 'cache-and-network',
  })

  useFocusEffect(
    React.useCallback(() => {
      refetch()
    }, [refetch])
  )

  useEffect(() => {
    if (error) {
      console.error('Error fetching user tokens:', error)
      Sentry.captureException(error)
    }
  }, [error])

  const tokens = data?.userTokens || []

  const paymentMethods = useMemo(
    (): Array<{ type: PaymentMethodRowType; visible: boolean }> => [
      {
        type: 'paypal',
        visible: isPaymentConnectionEnabled(PaymentConnectionType.PAYPAL),
      },
      {
        type: 'wechat',
        visible: isPaymentConnectionEnabled(PaymentConnectionType.WECHAT),
      },
    ],
    []
  )

  const visiblePaymentMethods = useMemo(
    () => paymentMethods.filter((method) => method.visible),
    [paymentMethods]
  )

  const handlePaypalConnect = () => {
    setShowPaypalModal(true)
  }

  const handlePaypalConnectSuccess = () => {
    refetch()
  }

  return (
    <ScreenWrapper withScrollView={true}>
      <Column gap={4} flex={1} style={styles.container}>
        <Row
          columnGap={4}
          wrap
          justifyContent="space-between"
          alignItems="center"
        >
          <IconButton
            mode={'outlined'}
            icon="arrow-left"
            onPress={() => navigation.goBack()}
          />
        </Row>

        <Column gap={2} style={styles.content}>
          <Text variant="bodyMedium" style={styles.description}>
            {t('description')}
          </Text>

          {!loading && visiblePaymentMethods.length === 0 ? (
            <NoDataMessage
              variant="standaloneIllustrated"
              labels={{ noDataMessage: t('no-payment-providers-available') }}
            />
          ) : null}

          {visiblePaymentMethods.map((method) => {
            const token = tokens.find((t) => t.source === method.type) || null
            return (
              <PaymentMethodRow
                key={method.type}
                type={method.type}
                token={token}
                onConnect={
                  method.type === 'paypal'
                    ? handlePaypalConnect
                    : () => {
                        navigation.navigate('BillingList')
                      }
                }
                onDisconnect={() => {
                  refetch()
                }}
                onSetDefault={() => {
                  refetch()
                }}
              />
            )
          })}
        </Column>
      </Column>

      <PaypalConnectModal
        visible={showPaypalModal}
        onDismiss={() => setShowPaypalModal(false)}
        onSuccess={handlePaypalConnectSuccess}
      />
    </ScreenWrapper>
  )
}

const useStyle = createStyle(({ theme }) => ({
  container: {
    padding: theme.spacing(2),
  },
  content: {
    marginTop: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(2),
    color: theme.colors.onSurfaceVariant,
  },
}))
